import React from 'react';
import { useIntl } from 'react-intl';
import { profileImageSpec } from 'fym-shared/src/music24/artistProfile';
import { TypedMemo } from '../../../../common/types';
import type { ProfileCardAction, ProfileCardModel, ProfileCardTileKey } from './profileCardModel';
import { ProfileCardFrame } from './ProfileCardFrame';
import { ProfileCardTile } from './ProfileCardTile';
import { TILE_TEXT } from './profileCardText';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole Profile entry card; each part uses its own slice of it */
import styles from './ProfileEntryCard.module.scss';
/* eslint-enable css-modules/no-unused-class */

const aspectOf = (kind: Parameters<typeof profileImageSpec>[0]) => {
  const spec = profileImageSpec(kind);
  return spec.width / spec.height;
};

/** The frames of the loading state (design p5): skeletons at the true aspect, so nothing moves when the data comes. */
const LOADING: ReadonlyArray<{ key: ProfileCardTileKey; aspects: number[] }> = [
  { key: 'spotify', aspects: [aspectOf('spotifyHeader'), 1] },
  { key: 'youtube', aspects: [aspectOf('youtubeBanner')] },
  { key: 'facebook', aspects: [aspectOf('facebookCover')] },
  { key: 'press', aspects: [aspectOf('pressPhoto')] },
  { key: 'bio', aspects: [1.9] },
];

interface ProfileCardStripProps {
  model: ProfileCardModel | null;
  ghostSrc: string | null;
  busy: boolean;
  copied: boolean;
  onAction: (action: ProfileCardAction) => void;
  onOpenTile: (key: ProfileCardTileKey) => void;
}

/** The strip of thumbnails (design `pc-strip`). Below 576 px it scrolls sideways at a fixed height. */
const ProfileCardStripComponent: React.FC<ProfileCardStripProps> = (props) => {
  const { model, ghostSrc, busy, copied, onAction, onOpenTile } = props;
  const intl = useIntl();
  if (model === null) {
    return (
      <div className={styles.strip} aria-busy="true" data-testid="profile-card-strip-loading">
        {LOADING.map((tile) => (
          <div
            key={tile.key}
            className={styles.tile}
            style={
              {
                '--grow': tile.aspects.reduce((sum, one) => sum + one, 0),
                '--count': tile.aspects.length,
              } as React.CSSProperties
            }
          >
            <div className={styles.frames}>
              {tile.aspects.map((aspect, index) => (
                <ProfileCardFrame
                  // eslint-disable-next-line react/no-array-index-key
                  key={index}
                  frame={{ aspect, round: tile.key === 'spotify' && index === 1, content: { type: 'loading' } }}
                  ghostSrc={null}
                  label={intl.formatMessage(TILE_TEXT[tile.key].name)}
                />
              ))}
            </div>
            <div className={styles.meta}>
              <div className={styles.nameRow}>
                <b className={styles.name}>{intl.formatMessage(TILE_TEXT[tile.key].name)}</b>
                <span className={styles.size}>{intl.formatMessage(TILE_TEXT[tile.key].size)}</span>
              </div>
              <span className={styles.chipSkeleton} />
            </div>
          </div>
        ))}
      </div>
    );
  }
  return (
    <div className={styles.strip} data-testid="profile-card-strip">
      {model.tiles.map((tile) => (
        <ProfileCardTile
          key={tile.key}
          tile={tile}
          ghostSrc={ghostSrc}
          busy={busy}
          copied={copied}
          onAction={onAction}
          onOpen={() => onOpenTile(tile.key)}
        />
      ))}
    </div>
  );
};

export const ProfileCardStrip = TypedMemo(ProfileCardStripComponent);
